import React from "react";
import PropTypes from "prop-types";
import { Button, Col, Container, Row } from "reactstrap";
import { reportError } from "../../helpers/errorReporter";

/**
 * Red de seguridad contra errores de render.
 *
 * React 18 (createRoot) desmonta el arbol completo ante cualquier error no
 * capturado, asi que sin este boundary cualquier TypeError deja pantalla en
 * blanco. Se monta en dos niveles:
 *
 *   variant="app"   -> src/index.js, envuelve toda la aplicacion.
 *   variant="page"  -> src/routes/middleware/Authmiddleware.js, dentro del
 *                      Layout, para que el sidebar y el header sobrevivan al
 *                      crash de una pantalla concreta.
 *
 * OJO: variant="page" tambien cubre las rutas publicas de kiosco
 * (/dashboard/:route_slug, /dash-user/:route_slug, /tablero/:url_route), asi
 * que el fallback no debe asumir que existe una sesion.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, reference: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // reportError nunca lanza ni muestra toasts; el `reference` es el folio que
    // el usuario puede dictar a soporte para localizar el registro.
    reportError(error, {
      origin: "render",
      componentStack: errorInfo?.componentStack,
    }).then((reference) => {
      if (reference) this.setState({ reference });
    });
  }

  // Se usa window.location y no el history de React Router porque el propio
  // router puede ser lo que crasheo.
  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/dashboard";
  };

  handleLogout = () => {
    localStorage.removeItem("authUser");
    window.location.replace("/login");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";
    const { error, reference } = this.state;

    return (
      <div className="my-5 pt-5">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mb-4">
                <h4 className="text-uppercase">Ocurrio un error inesperado</h4>
                <p className="text-muted mb-3">
                  La pantalla no se pudo mostrar. Puedes recargar o volver al
                  inicio. Si el problema continua, cierra sesion e ingresa de
                  nuevo.
                </p>

                {/* El detalle se muestra tambien en produccion: sin el, el
                    usuario solo puede reportar "se rompio". */}
                {error?.message && (
                  <p className="text-danger mb-2">
                    <strong>Detalle:</strong> {String(error.message)}
                  </p>
                )}

                {reference && (
                  <p className="text-muted mb-4">
                    Codigo de referencia: <strong>#{reference}</strong> —
                    proporcionalo a soporte para localizar el error.
                  </p>
                )}
                <div className="mt-4">
                  <Button color="primary" className="me-2" onClick={this.handleReload}>
                    Recargar
                  </Button>
                  <Button color="secondary" className="me-2" onClick={this.handleHome}>
                    Ir al inicio
                  </Button>
                  <Button color="light" onClick={this.handleLogout}>
                    Cerrar sesion
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {isDev && error && (
            <Row>
              <Col lg={12}>
                <pre
                  className="bg-light p-3 mt-3 text-danger"
                  style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
                >
                  {error.toString()}
                  {"\n\n"}
                  {error.stack}
                </pre>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.any,
  variant: PropTypes.oneOf(["app", "page"]),
};

export default ErrorBoundary;
